import React, { useRef, useState, useCallback, useEffect } from 'react';
import { ReactSketchCanvas } from 'react-sketch-canvas';
import './EyeCanvas.css';

const EyeCanvas = ({ eye, value, onChange }) => {
    const canvasRef = useRef(null);
    const exportTimerRef = useRef(null);
    const [strokeWidth, setStrokeWidth] = useState(2);
    const [strokeColor, setStrokeColor] = useState('#000000');
    const [eraseMode, setEraseMode] = useState(false);

    const handleUndo = () => {
        canvasRef.current?.undo();
        debouncedExport();
    };

    const handleRedo = () => {
        canvasRef.current?.redo();
        debouncedExport();
    };

    const handleClear = () => {
        if (window.confirm('¿Está seguro que desea borrar todo el dibujo?')) {
            canvasRef.current?.clearCanvas();
            debouncedExport();
        }
    };

    const handleEraser = () => {
        setEraseMode(!eraseMode);
    };

    const handleExportImage = useCallback(async () => {
        try {
            const imageData = await canvasRef.current?.exportImage('png');
            if (onChange) {
                onChange(imageData);
            }
        } catch (error) {
            console.error('Error exporting canvas:', error);
        }
    }, [onChange]);

    // Debounced export - only export after user stops drawing for 1 second
    const debouncedExport = useCallback(() => {
        if (exportTimerRef.current) {
            clearTimeout(exportTimerRef.current);
        }
        exportTimerRef.current = setTimeout(() => {
            handleExportImage();
        }, 1000);
    }, [handleExportImage]);

    // Export image when canvas changes (debounced)
    const handleCanvasChange = useCallback(() => {
        debouncedExport();
    }, [debouncedExport]);

    // Cleanup timer on unmount
    useEffect(() => {
        return () => {
            if (exportTimerRef.current) {
                clearTimeout(exportTimerRef.current);
            }
        };
    }, []);

    return (
        <div className="eye-canvas-container">
            <div className="canvas-header">
                <h4>{eye === 'right' ? 'Ojo Derecho (OD)' : 'Ojo Izquierdo (OI)'}</h4>
            </div>

            <div className="canvas-toolbar">
                <div className="toolbar-group">
                    <label>Grosor:</label>
                    <input
                        type="range"
                        min="1"
                        max="10"
                        value={strokeWidth}
                        onChange={(e) => setStrokeWidth(parseInt(e.target.value))}
                        style={{ width: '100px' }}
                    />
                    <span>{strokeWidth}px</span>
                </div>

                <div className="toolbar-group">
                    <label>Color:</label>
                    <input
                        type="color"
                        value={strokeColor}
                        onChange={(e) => setStrokeColor(e.target.value)}
                        disabled={eraseMode}
                    />
                </div>

                <div className="toolbar-group">
                    <button
                        type="button"
                        onClick={handleEraser}
                        className={`btn-tool ${eraseMode ? 'active' : ''}`}
                        title="Borrador"
                    >
                        🧹 Borrador
                    </button>
                </div>

                <div className="toolbar-group">
                    <button
                        type="button"
                        onClick={handleUndo}
                        className="btn-tool"
                        title="Deshacer"
                    >
                        ↶ Deshacer
                    </button>
                    <button
                        type="button"
                        onClick={handleRedo}
                        className="btn-tool"
                        title="Rehacer"
                    >
                        ↷ Rehacer
                    </button>
                </div>

                <div className="toolbar-group">
                    <button
                        type="button"
                        onClick={handleClear}
                        className="btn-tool btn-danger"
                        title="Borrar todo"
                    >
                        🗑️ Limpiar
                    </button>
                </div>
            </div>

            <div className="canvas-wrapper">
                <ReactSketchCanvas
                    ref={canvasRef}
                    width="100%"
                    height="400px"
                    strokeWidth={strokeWidth}
                    strokeColor={eraseMode ? 'white' : strokeColor}
                    backgroundImage="/images/eye-diagram.svg"
                    exportWithBackgroundImage={true}
                    onChange={handleCanvasChange}
                    canvasColor="transparent"
                    preserveBackgroundImageAspectRatio="none"
                    allowOnlyPointerType="all"
                    style={{
                        border: '2px solid #5f91c5',
                        borderRadius: '8px',
                        backgroundColor: '#ffffff',
                        touchAction: 'none'
                    }}
                />
            </div>

            <div className="canvas-info">
                <small style={{ color: '#666' }}>
                    💡 Dibuje directamente sobre la imagen para marcar hallazgos.
                    Los dibujos se guardan automáticamente 1 segundo después de terminar de dibujar.
                </small>
            </div>
        </div>
    );
};

export default EyeCanvas;
